/**
 * Room Resolver Service
 * Resolves room identifiers (volatile roomId or stable customRoomId) to the current Colyseus roomId.
 * Used for join links that may contain stale IDs after server restart.
 */

import { getServerBaseUrl } from '../utils/serverUrl'
import store from '../stores'
import { withBearerAuth } from '../utils/authHeaders'

export interface ResolveRoomPayload {
  roomId?: string
  customRoomId?: string
  companyId?: string | null
  /**
   * Set only when the join comes from an invite link. The server may then start a room that is
   * not running for a holder of a valid open invite. Never log it.
   */
  inviteToken?: string | null
  /**
   * The guest's own `mtg_` meeting token. A guest has no session, so this is the credential the
   * server accepts for a read-only lookup of the booked room, sent as the Bearer header (never in
   * the URL, where proxies and access logs keep it). Only the guest join sets it. Never log it.
   */
  meetingToken?: string | null
}

const MAX_MEETING_TOKEN_LENGTH = 256

export function isMeetingToken(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('mtg_') &&
    value.length <= MAX_MEETING_TOKEN_LENGTH
  )
}

export interface ResolvedRoom {
  roomId: string
  name?: string
  companyId?: string | null
  customRoomId?: string
  metadata?: Record<string, unknown>
}

function toResolvedRoom(room: Record<string, any>): ResolvedRoom {
  return {
    roomId: room.roomId,
    name: room.name,
    companyId: room.companyId ?? null,
    customRoomId: room.customRoomId,
    metadata: room.metadata,
  }
}

function canResolveRoomForUser(roomCompanyId: string | null, userCompanyId: string | null): boolean {
  if (roomCompanyId !== null && userCompanyId !== null && roomCompanyId !== userCompanyId) {
    return false
  }

  return true
}

/**
 * Resolve a room for joining. Tries availableRooms first, then the resolve API.
 * @param payload - Either { roomId } (legacy) or { customRoomId, companyId } (stable format)
 * @param userCompanyId - The joining user's company ID (for validation)
 * @returns Resolved room data with current roomId, or null if not found
 */
export async function resolveRoomForJoin(
  payload: ResolveRoomPayload,
  userCompanyId: string | null
): Promise<ResolvedRoom | null> {
  const availableRooms = store.getState().room.availableRooms

  // 1. If we have roomId, check availableRooms first
  if (payload.roomId) {
    const roomInList = availableRooms.find((r: { roomId: string }) => r.roomId === payload.roomId)
    if (roomInList) {
      return toResolvedRoom({
        roomId: roomInList.roomId,
        name: roomInList.name,
        companyId: roomInList.metadata?.companyId ?? null,
        customRoomId: roomInList.metadata?.customRoomId,
        metadata: roomInList.metadata,
      })
    }
  }

  // 2. If we have customRoomId and companyId, call resolve API
  if (payload.customRoomId && payload.companyId !== undefined) {
    try {
      const baseUrl = getServerBaseUrl()
      const params = new URLSearchParams()
      params.set('customRoomId', payload.customRoomId)
      params.set('companyId', payload.companyId ?? '')
      // One credential per call: an invite link wins, so the two tokens never travel together.
      const guestToken =
        !payload.inviteToken && isMeetingToken(payload.meetingToken) ? payload.meetingToken : null
      if (payload.inviteToken) params.set('inviteToken', payload.inviteToken)
      const url = `${baseUrl}/api/lobby/rooms/resolve?${params}`
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)
      try {
        // The route needs a session (a member; map editor rooms for platform admins only) or, for
        // a guest, the `mtg_` token as the Bearer. A guest call sends the meeting token instead of
        // any session another tab left in storage, so the server always takes the guest branch.
        const baseHeaders = { 'Content-Type': 'application/json' }
        const headers = guestToken
          ? { ...baseHeaders, Authorization: `Bearer ${guestToken}` }
          : withBearerAuth(baseHeaders)
        const response = await fetch(url, {
          method: 'GET',
          headers,
          signal: controller.signal,
        })

        if (!response.ok) {
          if (response.status === 404) return null
          console.warn('[roomResolver] Resolve API error:', response.status)
          return null
        }

        const data = await response.json()
        if (!data.success || !data.room) return null

        const room = data.room
        const roomCompanyId = room.companyId ?? null
        // A holder of an invite may join a room of another company: the server checked the invite.
        if (!payload.inviteToken && !canResolveRoomForUser(roomCompanyId, userCompanyId)) {
          return null
        }

        return toResolvedRoom(room)
      } finally {
        clearTimeout(timeoutId)
      }
    } catch (err) {
      console.error('[roomResolver] Failed to resolve room:', err)
      return null
    }
  }

  return null
}
